import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useStorage } from '../storage/StorageContext';
import Header from '../components/Header';
import RosterTab from './RosterTab';
import TacticsTab from './TacticsTab';
import { Team } from '../types';
import { useDialog } from '../components/DialogProvider';
import { getTierBadge } from '../components/CreateTeamModal';
import teamsData from '../data/teams_data.json';
import '../App.css';

interface TacticsPageProps {
  defaultTab?: 'roster' | 'tactics';
}

export default function TacticsPage({ defaultTab = 'tactics' }: TacticsPageProps) {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { teamId } = useParams<{ teamId: string }>();
  // New team config is only passed via state on the initial creation navigate from HomePage
  const newTeamConfig = (state as any)?.newTeam as { teamName: string, region: string, rulesetMode: 'league' | 'exhibition', goldBudget: number, tier: number } | undefined;
  const storage = useStorage();
  const { alert, confirm, toast } = useDialog();

  const [activeTab, setActiveTab] = useState<'roster' | 'tactics'>(defaultTab);
  const [team, setTeam] = useState<Team | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [leagues, setLeagues] = useState<{name: string, link: string}[]>([]);

  // Default structure for a brand new team
  const defaultNewTeam = (): Team => ({
    id: '',
    teamName: '',
    region: 'Unknown Region',
    coachName: '',
    players: Array.from({ length: 16 }, (_, i) => ({
      number: i + 1,
      name: '',
      position: '',
    })),
    formations: [],
    staffCounts: {},
    inducementCounts: {},
    selectedStarPlayers: {},
    rulesetMode: 'league',
    goldBudget: 1000000,
    treasury: 1000000,
    tier: 1,
    createdAt: '',
    updatedAt: '',
  });

  useEffect(() => {
    if (!teamId) {
      // In new team creation mode, load default empty layout
      const baseTeam = defaultNewTeam();
      if (newTeamConfig) {
        baseTeam.teamName = newTeamConfig.teamName;
        baseTeam.region = newTeamConfig.region;
        baseTeam.rulesetMode = newTeamConfig.rulesetMode;
        baseTeam.goldBudget = newTeamConfig.goldBudget;
        baseTeam.treasury = newTeamConfig.goldBudget;
        baseTeam.tier = newTeamConfig.tier;
      }
      setTeam(baseTeam);
      return;
    }

    // In load team mode, fetch from persistent IndexedDB
    storage.listTeams().then((teams) => {
      const match = teams.find((x) => x.id === teamId) || null;
      if (match) {
        setTeam(match);
      } else {
        setErrorMessage('Saved team details not found.');
      }
    }).catch(err => {
      console.error('Failed to load team:', err);
      setErrorMessage('Could not load team from local storage.');
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [teamId, storage]);

  useEffect(() => {
    if (team && team.region) {
      import(`../data/${team.region.replace(/\s+/g, '')}.json`)
        .then((data) => {
          const loadedLeagues = data.default?.Leagues || data.Leagues || [];
          setLeagues(loadedLeagues);
          if (loadedLeagues.length > 0 && !team.selectedLeague) {
            setTeam((prev) => prev ? { ...prev, selectedLeague: loadedLeagues[0].name } : prev);
          }
        })
        .catch(() => setLeagues([]));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team?.region]);

  // Auto-save effect (debounced 800ms to avoid a write on every keystroke)
  useEffect(() => {
    if (!team) return;
    const timer = setTimeout(() => {
      storage.saveTeam(team).then(id => {
        if (!team.id) {
          // First save: update state and move to the URL-based route so refresh works
          setTeam(prev => prev ? { ...prev, id } : null);
          navigate(`/roster/${id}`, { replace: true });
        }
      }).catch(err => {
        console.error('Auto-save failed:', err);
      });
    }, 800);
    return () => clearTimeout(timer);
  }, [team, storage, navigate]);

  const handleRegister = async () => {
    if (!team) return;

    // Validate the team name
    if (!team.teamName.trim()) {
      setErrorMessage('Team name is required to register.');
      return;
    }

    // Validate roster size for legal registration
    const activePlayers = team.players.filter((player) => player.position).length;
    if (activePlayers < 11) {
      setErrorMessage('At least 11 players are required to register.');
      return;
    }

    if ((team.treasury ?? 0) < 0) {
      setErrorMessage('Treasury cannot be negative when registering a team.');
      return;
    }

    if (!await confirm("Are you sure you want to register this team? You are locking down the team and no more initial draft changes can be made.", "Register Team")) {
      return;
    }

    try {
      const updatedTeam = { ...team, isRegistered: true };
      const id = await storage.saveTeam(updatedTeam);
      updatedTeam.id = id;
      setTeam(updatedTeam);
      toast('Team registered successfully!', 'success');
      setErrorMessage(null);
    } catch (error) {
      console.error('Failed to register team:', error);
      setErrorMessage('Failed to register the team. Please try again.');
    }
  };

  const handleUnregister = async () => {
    if (!team || !team.isRegistered) return;

    if (!await confirm(
      'Switch this team back to Draft mode? This re-enables roster drafting actions and treasury draft adjustments.',
      'Un-register Team'
    )) {
      return;
    }

    try {
      const updatedTeam = { ...team, isRegistered: false };
      const id = await storage.saveTeam(updatedTeam);
      updatedTeam.id = id;
      setTeam(updatedTeam);
      toast('Team moved back to Draft mode.', 'success');
      setErrorMessage(null);
    } catch (error) {
      console.error('Failed to un-register team:', error);
      setErrorMessage('Failed to update team status. Please try again.');
    }
  };

  if (errorMessage && !team) {
    return (
      <>
        <Header status="Workspace Error" />
        <div style={{ textAlign: 'center', marginTop: '100px', padding: '20px' }}>
          <p style={{ color: 'red', fontSize: '18px', fontWeight: 'bold' }}>{errorMessage}</p>
          <button className="home-btn" onClick={() => navigate('/')}>Back to Home</button>
        </div>
      </>
    );
  }

  if (!team) {
    return (
      <>
        <Header status="Loading Workspace..." />
        <div style={{ textAlign: 'center', marginTop: '100px', fontSize: '18px', color: '#666' }}>
          Loading team details...
        </div>
      </>
    );
  }

  const factionData = team ? teamsData.find((t: any) => t.region === team.region || t.team_name === team.region) : null;
  const specialRules = (factionData as any)?.special_rules || [];

  const handleHelpClick = async () => {
    await alert(
      "Coaching Board Controls:\n\n• Tap a bench player to select, then tap an empty cell on your half (rows 14-26) to place.\n• Tap a placed player once to view their stats card.\n• Tap the floating stats card to close/dismiss it.\n• Tap the active selected player on the field again to recall them to the bench.\n• Toggling 'Offensive'/'Defensive' will safely filter your custom formations without clearing the board.",
      "Coaching Board Controls"
    );
  };

  const activePlayers = team.players.filter((player) => player.position).length;
  const registerValidationIssues: string[] = [];
  if (!team.teamName.trim()) registerValidationIssues.push('Team name is required.');
  if (activePlayers < 11) registerValidationIssues.push('At least 11 players are required.');
  if ((team.treasury ?? 0) < 0) registerValidationIssues.push('Treasury cannot be negative.');
  const canRegister = registerValidationIssues.length === 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', paddingBottom: '32px' }}>
      <Header status={
        <div style={{ display: 'flex', flexDirection: 'column', width: '100%', gap: '8px' }}>
          {/* ROW 1: Identity & Primary Actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', flexWrap: 'wrap', gap: '12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <input
                type="text"
                value={team.teamName}
                onChange={(e) => setTeam({ ...team, teamName: e.target.value })}
                placeholder="Enter team name"
                style={{ 
                  background: 'transparent', 
                  border: 'none', 
                  color: '#ffffff', 
                  fontSize: '16px', 
                  fontWeight: 'bold', 
                  outline: 'none',
                  padding: '2px 6px',
                  borderBottom: '1px solid rgba(255,255,255,0.3)',
                  width: '180px',
                  flexShrink: 0
                }}
              />
              
              <span style={{ fontSize: '13px', fontWeight: '500', flexShrink: 0, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                <span>{team.region}</span>
                {team.tier ? (() => {
                  const badge = getTierBadge(team.tier);
                  return (
                    <span style={{
                      background: badge.background,
                      color: badge.color,
                      padding: '1px 6px',
                      borderRadius: '3px',
                      fontSize: '10px',
                      fontWeight: 'bold',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                      display: 'inline-block'
                    }}>
                      {badge.label}
                    </span>
                  );
                })() : null}
              </span>

              <span style={{ opacity: 0.3, fontSize: '13px', color: '#ffffff' }}>|</span>

              {/* Budget */}
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#cbd5e1', whiteSpace: 'nowrap' }}>
                💰 Budget: {(team.goldBudget || 1000000).toLocaleString()} gp
              </span>
            </div>
          </div>

          {/* ROW 2: Setup, Status & Actions (Sequential Flow Layout) */}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            width: '100%', 
            flexWrap: 'wrap', 
            gap: '16px', 
            marginTop: '4px',
            borderTop: '1px solid rgba(255,255,255,0.05)',
            paddingTop: '6px'
          }}>
            {/* League selection */}
            {leagues.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontSize: '13px', fontWeight: 'bold', color: '#cbd5e1' }}>🏅 League:</span>
                {leagues
                  .filter(l => !team.isRegistered || team.selectedLeague === l.name)
                  .map((l) => (
                  <button
                    key={l.name}
                    disabled={team.isRegistered}
                    onClick={() => setTeam({ ...team, selectedLeague: l.name })}
                    style={{
                      background: team.selectedLeague === l.name ? '#4f46e5' : '#e2e8f0',
                      color: team.selectedLeague === l.name ? 'white' : '#64748b',
                      border: 'none',
                      borderRadius: '12px',
                      padding: '2px 10px',
                      fontSize: '12px',
                      fontWeight: 'bold',
                      cursor: team.isRegistered ? 'default' : 'pointer',
                      opacity: team.isRegistered && team.selectedLeague !== l.name ? 0.5 : 1,
                      transition: 'all 0.2s'
                    }}
                  >
                    {l.name}
                  </button>
                ))}
              </div>
            )}

            {/* Special Rules */}
            {specialRules.length > 0 && (
              <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
                {specialRules.map((rule: any, idx: number) => (
                  <span key={idx} style={{ 
                    background: 'rgba(255,255,255,0.08)', 
                    padding: '2px 8px', 
                    borderRadius: '12px', 
                    fontSize: '11px',
                    color: '#94a3b8',
                    whiteSpace: 'nowrap'
                  }}>
                    {rule.name}
                  </span>
                ))}
              </div>
            )}

            {/* Status Badge */}
            <button
              type="button"
              onClick={team.isRegistered ? handleUnregister : undefined}
              title={team.isRegistered ? 'Click to switch back to Draft mode' : 'Draft mode'}
              style={{
                fontSize: '11px',
                fontWeight: 'bold',
                padding: '2px 8px',
                borderRadius: '4px',
                backgroundColor: team.isRegistered ? 'rgba(34, 197, 94, 0.15)' : 'rgba(234, 88, 12, 0.15)',
                color: team.isRegistered ? '#4ade80' : '#f97316',
                border: team.isRegistered ? '1px solid rgba(34, 197, 94, 0.3)' : '1px solid rgba(234, 88, 12, 0.3)',
                whiteSpace: 'nowrap',
                cursor: team.isRegistered ? 'pointer' : 'default',
                appearance: 'none'
              }}
            >
              {team.isRegistered ? '✓ Registered' : 'Draft'}
            </button>

            {/* Register button right next to Status */}
            {!team.isRegistered && (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                {errorMessage && (
                  <span style={{ color: '#fca5a5', fontSize: '11px', fontWeight: 'bold' }}>
                    {errorMessage}
                  </span>
                )}
                <button
                  onClick={handleRegister}
                  disabled={!canRegister}
                  title={!canRegister ? registerValidationIssues.join(' ') : 'Register this team'}
                  style={{
                    backgroundColor: canRegister ? '#ea580c' : '#9ca3af',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '3px 12px',
                    fontWeight: 'bold',
                    cursor: canRegister ? 'pointer' : 'not-allowed',
                    fontSize: '11px',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.3)',
                    transition: 'background-color 0.15s ease'
                  }}
                  onMouseOver={(e) => {
                    if (canRegister) e.currentTarget.style.backgroundColor = '#f97316';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.backgroundColor = canRegister ? '#ea580c' : '#9ca3af';
                  }}
                >
                  Register
                </button>
              </div>
            )}
          </div>
        </div>
      } />

      {/* Removed Persistent Banner */}
      
      {/* Workspace Tabs — aligned inside the 1200px panel constraint */}
      <div className="desktop-top-tabs" style={{ maxWidth: '1240px', width: '100%', margin: '0 auto', padding: '24px 20px 0 20px', boxSizing: 'border-box' }}>
        <div className="workspace-tabs" style={{ display: 'flex', alignItems: 'center' }}>
          <button
            className={`workspace-tab-btn ${activeTab === 'roster' ? 'active' : ''}`}
            onClick={() => setActiveTab('roster')}
          >
            📋 Roster
          </button>
          <button
            className={`workspace-tab-btn ${activeTab === 'tactics' ? 'active' : ''}`}
            onClick={() => setActiveTab('tactics')}
          >
            🏈 Tactics
          </button>

          {/* Global Help Button aligned inside the empty space to the right */}
          <button
            onClick={handleHelpClick}
            style={{
              marginLeft: 'auto',
              background: '#f8fafc',
              border: '1px solid #cbd5e1',
              borderRadius: '6px',
              padding: '6px 12px',
              fontSize: '13px',
              fontWeight: 'bold',
              color: '#475569',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            ❓ Help
          </button>
        </div>
      </div>

      {/* Mobile Bottom Navigation */}
      <div className="mobile-bottom-nav">
        <button
          className={activeTab === 'roster' ? 'active' : ''}
          onClick={() => setActiveTab('roster')}
        >
          📋 Roster
        </button>
        <button
          className={activeTab === 'tactics' ? 'active' : ''}
          onClick={() => setActiveTab('tactics')}
        >
          🏈 Tactics
        </button>
        <button onClick={handleHelpClick}>
          ❓ Help
        </button>
      </div>

      {/* Tab Contents */}
      <div className="tab-contents-wrapper" style={{ flex: 1 }}>
        {activeTab === 'roster' ? (
          <RosterTab team={team} onChange={setTeam} />
        ) : (
          <TacticsTab team={team} onChange={setTeam} />
        )}
      </div>
    </div>
  );
}
